import { useMemo } from 'react';
import { isEmpty } from 'lodash';
import { isPast } from 'date-fns';

import { useAppSelector } from '../redux';
import { userSelectors } from '../redux/user';
import { INavigationStep } from '../types';
import {
  APP_ROUTES,
  IntermediateScreen,
  KnowledgeQuizStatus,
  TEST_TASK_STATUS,
  TestTaskType,
} from '../constants';
import { useUserTestTasks } from './useUserTestTasks';
import { userRouteSelector } from '../redux/userRoute';
import { useUserVideoInterview } from './useUserVideoInterview';
import { ReactComponent as VideoCameraIcon } from '../assets/video-camera-icon.svg';
import { useKnowledgeQuiz } from './useKnowledgeQuiz';
import { useIsIndependentVIPage } from './useIsIndependentVIPage';
import { useUserMeetings } from './useUserMeetings';

const QuestioneerStepperPages = [
  APP_ROUTES.EMAIL,
  APP_ROUTES.NAME,
  APP_ROUTES.COUNTRY,
  APP_ROUTES.CITY,
  APP_ROUTES.OFFICE,
  APP_ROUTES.ENGLISH_LEVEL,
  APP_ROUTES.SPECIALIZATION,
  APP_ROUTES.TECH_SKILLS,
  APP_ROUTES.PRIMARY_SKILLS,
  APP_ROUTES.SECONDARY_SKILLS,
  APP_ROUTES.EXPERIENCE,
  APP_ROUTES.SENIORITY,
  APP_ROUTES.ENGAGEMENT,
  APP_ROUTES.COMPENSATION,
  APP_ROUTES.NOTICE_PERIOD,
  APP_ROUTES.PROFESSIONAL_IDENTITY,
  APP_ROUTES.PROFILE_REVIEW,
  APP_ROUTES.ABOUT_YOURSELF,
];

const VideoInterviewPages = [
  APP_ROUTES.VIDEO_INTERVIEW_SELECTION,
  APP_ROUTES.VIDEO_INTERVIEW_FINISHED,
  APP_ROUTES.RECORD_VIDEO,
  APP_ROUTES.CANDIDATE_VIDEO_INTERVIEW_START,
  APP_ROUTES.CANDIDATE_VIDEO_INTERVIEW_RECORD,
];

const KnowledgeQuizPages = [APP_ROUTES.KNOWLEDGE_QUIZ];

const TestTaskPages = [
  APP_ROUTES.TEST_TASK_SELECTION,
  APP_ROUTES.QUIZ_TEST_TASK,
  APP_ROUTES.CODILITY_TEST_TASK,
  APP_ROUTES.HACKER_RANK_TEST_TASK,
  APP_ROUTES.INTERNAL_TEST_TASK,
  APP_ROUTES.INTERNAL_TEST_TASK_SENT,
];

const TechnicalInterviewPages = [APP_ROUTES.TECHNICAL_INTERVIEW];

export const useMainNavigationSteps = () => {
  const userData = useAppSelector(userSelectors.getUserData);
  const stepperRoute = useAppSelector(userRouteSelector.getUserRoute);

  const { knowledgeQuiz } = useKnowledgeQuiz();
  const isIndependentVIPage = useIsIndependentVIPage();
  const {
    availableVideoInterview,
    userVideoInterview,
    isUserQuitedVideoInterview,
    isUserSkippedVideoInterview,
  } = useUserVideoInterview();
  const { availableTestTasks } = useUserTestTasks();
  const { activeTechnicalInterview } = useUserMeetings();

  const internalTestTask = userData?.testTasks?.find(
    (testTask) => testTask.testTaskType === TestTaskType.INTERNAL,
  );

  const isQuestioneerPage = QuestioneerStepperPages.includes(stepperRoute);
  const isVIPage = VideoInterviewPages.includes(stepperRoute);
  const isKnowledgeQuizPage = KnowledgeQuizPages.includes(stepperRoute);
  const isTestTaskPage = TestTaskPages.includes(stepperRoute);
  const isTICallPage = TechnicalInterviewPages.includes(stepperRoute);

  const mainNavigationSteps = useMemo(() => {
    let numeration = 1;
    const steps: INavigationStep[] = [];

    steps.push({
      label: 'Tell us about yourself',
      numeration: numeration,
      isActive: isQuestioneerPage,
      isCompleted: !!userData?.profileSubmittedAt,
      isNavigatable: !!userData?.profileSubmittedAt,
      route: APP_ROUTES.ABOUT_YOURSELF,
    });
    numeration++;

    if (
      userData?.intermediateScreen === IntermediateScreen.FirstStepIsDone &&
      userData.crucialRequirements &&
      !userData.crucialRequirementsSubmittedAt
    ) {
      steps.push({
        label: 'Confirm requirements',
        numeration: numeration,
        isActive: true,
        isCompleted: false,
        isNavigatable: false,
      });
      numeration++;
      steps.push({
        label: 'Proceed with next steps',
        numeration: numeration,
        isActive: false,
        isCompleted: false,
        isNavigatable: false,
      });
      numeration++;
      return steps;
    }

    if (
      userData?.intermediateScreen &&
      userData?.intermediateScreen !== IntermediateScreen.AEBenefits
    )
      return [];

    if (
      !isEmpty(userData?.crucialRequirements) &&
      userData?.crucialRequirementsSubmittedAt
    ) {
      steps.push({
        label: 'Confirm requirements',
        numeration: numeration,
        isActive: true,
        isCompleted: true,
        isNavigatable: false,
      });
      numeration++;
    }

    if (knowledgeQuiz) {
      steps.push({
        label: 'Take a 5 min knowledge quiz',
        numeration: numeration,
        isActive: isKnowledgeQuizPage,
        isCompleted: [
          KnowledgeQuizStatus.Passed,
          KnowledgeQuizStatus.Failed,
        ].includes(knowledgeQuiz.status),
        route: isKnowledgeQuizPage ? stepperRoute : undefined,
      });
      numeration++;
    }

    if (!internalTestTask && availableTestTasks.length) {
      // PLP-2482: show the stage as explicitly skipped, the same way the VI step does.
      const isTTSkipped = availableTestTasks.every(
        (tt) => tt.status === TEST_TASK_STATUS.SKIPPED,
      );

      steps.push({
        label: 'Pass a short test',
        numeration: numeration,
        isActive: isTestTaskPage,
        isSkipped: !isTestTaskPage && isTTSkipped,
        isCompleted:
          !isTTSkipped &&
          availableTestTasks.some((tt) =>
            [
              TEST_TASK_STATUS.DONE,
              TEST_TASK_STATUS.FAILED,
              TEST_TASK_STATUS.PASSED,
            ].includes(tt.status),
          ),
        route: isTestTaskPage ? stepperRoute : undefined,
      });
      numeration++;
    }

    if (
      availableVideoInterview?.length ||
      userVideoInterview ||
      isIndependentVIPage
    ) {
      const isVISkipped = isUserSkippedVideoInterview;
      const isVIQuited = isUserQuitedVideoInterview;

      const viStep = {
        label: 'Record a short video',
        icon: VideoCameraIcon,
        numeration: numeration,
        isActive: isVIPage,
        isNavigatable: !isVIPage && (isVIQuited || isVISkipped),
        isCompleted: userVideoInterview && !(isVISkipped || isVIQuited),
        isSkipped: !isVIPage && (isVISkipped || isVIQuited),
        route: isVIPage
          ? stepperRoute
          : isVISkipped
          ? APP_ROUTES.VIDEO_INTERVIEW_SELECTION
          : APP_ROUTES.VIDEO_INTERVIEW_FINISHED,
      };

      if (isIndependentVIPage) {
        return [
          {
            ...viStep,
            numeration: 1,
          },
        ];
      }

      steps.push(viStep);
      numeration++;
    }

    if (internalTestTask) {
      steps.push({
        label: 'Pass a short test',
        numeration: numeration,
        isActive: isTestTaskPage,
        isCompleted: internalTestTask.status.some((status) =>
          [
            TEST_TASK_STATUS.DONE,
            TEST_TASK_STATUS.FAILED,
            TEST_TASK_STATUS.PASSED,
          ].includes(status),
        ),
        route: isTestTaskPage ? stepperRoute : undefined,
      });
      numeration++;
    }

    steps.push({
      label: 'Ace the interview with our team',
      numeration: numeration,
      isActive: isTICallPage,
      isCompleted:
        !!activeTechnicalInterview &&
        !activeTechnicalInterview.canceled &&
        isPast(new Date(activeTechnicalInterview.scheduledDate)),
    });
    numeration++;

    steps.push({
      label: 'Get an offer',
      numeration: numeration,
    });
    return steps;
  }, [userData, availableTestTasks, activeTechnicalInterview]);

  return {
    mainNavigationSteps,
  };
};
